import React, {useRef, useState} from 'react';
import apiClient from "../../../services/api-client";
import CreateNewUser from '../../../features/admin/components/user-management/CreateNewUser'
import EditUser from '../../../features/admin/components/user-management/EditEmployees'
import AppDialog from '../../../components/ui/AppDialog';
import {Toast} from 'primereact/toast';
import DataTableCard from '../../../components/ui/DataTableCard';
import {useFetchList} from '../../../hooks/useFetchList';
import {ConfirmPopup, confirmPopup} from 'primereact/confirmpopup';
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";

const EmployeesProfileReportTable = () => {
    const {rows: employees} = useFetchList('users/employees');
    const toast = useRef(null);
    const [activeUser, setActiveUser] = useState(null)

    const saveUser = async (data) => {
        await apiClient.put('user/edit', data).then((response) => {
            if (response.data.status === "SUCCESS") {
                toast.current.show({
                    severity: 'success',
                    summary: 'Updated',
                    detail: data.isActive ? 'You successfully activated this profile' : 'You successfully disabled the profile',
                    life: 3000
                });
                window.location.reload(true);
            } else {
                toast.current.show({severity: 'warn', summary: 'Failed', detail: response.data.message, life: 3000});
            }
        });
    }

    const handleActive = async (data) => {
        const userPayload = {
            "userRole": {
                "id": data.userRole.id,
            },
            "user": {
                "userId": data.id,
                "emailAddress": data.emailAddress,
                "phoneNumber": data.phoneNumber,
                "firstName": data.firstName,
                "lastName": data.lastName,
                "IDNumber": data.idnumber,
                "isActive": !data.isActive,
                "locked": data.enabled,
                "enabled": data.enabled,
                "userType": data.userType,
                "userRole": data.userRole.id,
            }
        };
        await saveUser(userPayload);
    }

    const handleLock = async (data) => {
        const userPayload = {
            "userRole": {"id": data.userRole.id},
            "user": {
                "userId": data.id,
                "emailAddress": data.emailAddress,
                "phoneNumber": data.phoneNumber,
                "firstName": data.firstName,
                "lastName": data.lastName,
                "IDNumber": data.idnumber,
                "isActive": data.isActive,
                "locked": !data.locked,
                "enabled": data.enabled,
                "userType": data.userType,
                "userRole": data.userRole.id,
            }
        };
        await saveUser(userPayload);
    }

    const rejectFunction = () => {
        toast.current.show({severity: 'warn', summary: 'Rejected', detail: 'You have rejected', life: 3000});
    };

    function renderUserType(rowData) {
        return <span className="badge badge-outline px-3 py-1">{rowData.userRole?.name}</span>;
    }

    function renderActions(rowData) {
        return (
            <div className="actions-container flex gap-2">
                <div className="tooltip" data-tip="Edit Profile">
                    <button
                        className="btn btn-sm btn-circle btn-ghost text-base-content/70 hover:text-base-content hover:bg-base-200"
                        onClick={() => {
                            setActiveUser(rowData);
                            setDialogVisible(true);
                        }}
                    >
                        <i className="pi pi-user-edit text-lg"></i>
                    </button>
                </div>

                <div className="tooltip" data-tip={rowData.isActive ? "Disable User" : "Activate User"}>
                    <button
                        className="btn btn-sm btn-circle btn-ghost text-base-content/70 hover:text-base-content hover:bg-base-200"
                        onClick={(event) => {
                            confirmPopup({
                                target: event.currentTarget,
                                message: rowData.isActive ? "Are you sure you want to disable this?" : "Are you sure you want to activate this?",
                                icon: "pi pi-exclamation-triangle",
                                accept: () => handleActive(rowData),
                                reject: rejectFunction
                            });
                        }}
                    >
                        <i className={`pi ${rowData.isActive ? 'pi-ban' : 'pi-check-circle'} text-lg`}></i>
                    </button>
                </div>

                <div className="tooltip" data-tip={rowData.isLocked ? "Unlock User" : "Lock User"}>
                    <button
                        className="btn btn-sm btn-circle btn-ghost text-base-content/70 hover:text-base-content hover:bg-base-200"
                        onClick={(event) => {
                            confirmPopup({
                                target: event.currentTarget,
                                message: rowData.isLocked ? "Are you sure you want to unlock this?" : "Are you sure you want to lock this?",
                                icon: "pi pi-exclamation-triangle",
                                accept: () => handleLock(rowData),
                                reject: rejectFunction
                            });
                        }}
                    >
                        <i className={`pi ${rowData.isLocked ? 'pi-unlock' : 'pi-lock'} text-lg`}></i>
                    </button>
                </div>
            </div>
        );
    }

    const [dialogVisible, setDialogVisible] = useState(false);

    const handleCreateUser = async () => {
        setDialogVisible(false)
        await toast.current.show({severity: 'success', summary: 'Success', detail: 'User registered successfully'});
    }

    const cols = [
        {field: 'firstName', header: 'First name'},
        {field: 'lastName', header: 'Last name'},
        {field: 'userRole.name', header: 'User Type', body: renderUserType},
        {field: 'emailAddress', header: 'Email'},
        {field: 'phoneNumber', header: 'Phone number'},
        {field: 'actions', header: 'Actions', body: renderActions, style: {textAlign: 'center'}},
    ];

    const createButton = (
        <button
            className="btn btn-primary h-10 min-h-0 text-white font-semibold shadow-sm rounded-full"
            onClick={() => {
                setActiveUser(null);
                setDialogVisible(true);
            }}
        >
            <i className="pi pi-user-plus"></i> Create new
        </button>
    );

    return (
        <div className="w-full">
            <Toast ref={toast}/>

            {activeUser === null ? (
                <AppDialog header="Register new user" visible={dialogVisible} boxClassName="w-11/12 max-w-3xl"
                        onHide={() => {
                            setActiveUser(null)
                            setDialogVisible(false)
                        }}>
                    <CreateNewUser onCloseDialog={handleCreateUser} style={{marginTop: "20px"}}/>
                </AppDialog>
            ) : (
                <AppDialog header="Edit user" visible={dialogVisible} boxClassName="w-11/12 max-w-3xl" onHide={() => {
                    setActiveUser(null)
                    setDialogVisible(false)
                }}>
                    <EditUser employeeData={activeUser} onCloseDialog={handleCreateUser} style={{marginTop: "20px"}}/>
                </AppDialog>
            )}

            <ConfirmPopup/>

            <DataTableCard
                title="Employees Profile Report"
                exportName="Employees"
                columns={cols}
                rows={employees}
                emptyMessage="No employees found"
                toolbarExtras={createButton}
                exportDisabled={false}
            />
        </div>
    );
};

export default EmployeesProfileReportTable;
